import { useRef, useState, useMemo } from "react";
import { Card, Flex, Row, Col, Skeleton, InputRef } from "antd";
import { WalletOutlined, ShoppingOutlined, CrownOutlined } from "@ant-design/icons";
import MetricCard from "components/MetricCard.component";
import { getPackageAnalyticsOverview } from "hooks/queries/analytics";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, formatUpgrades } from "shared/utils";
import { tnum } from "shared/components/TableText";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { getYtdDayRange } from "shared/utils/dayjs";
import {
  AnalyticsRange,
  CustomDayRange,
  PackageAnalyticsRow,
  PackageType,
} from "shared/types";
import RangeSegmented from "./RangeSegmented";
import CustomRangePicker from "./CustomRangePicker";
import ReportPageHeading from "./ReportPageHeading";
import ExportMenu from "./ExportMenu.component";
import { useAuth } from "context/AuthContextProvider";
import { useTableSort } from "hooks/useTableSort";
import { analyticsRangeStamp } from "shared/utils/download";
import { MAX_PDF_ROWS } from "pdf/layout";
import { loadReportPdf } from "shared/utils/loadPdfChunk";
import { ReportColumn, reportColumn } from "shared/reports/reportColumns";
import { ReportTable } from "shared/reports/ReportTable";
import { pendapatanProdukSpec } from "pdf/reports/specs/archetype1";
import { useReportPageCount } from "./useReportPageCount";
import { ProductNameCell } from "./ProductNameCell";
import { textPx } from "shared/constants/zoom";

interface PendapatanProdukContentProps {
  type: PackageType;
  title: string;
  backTo: string;
}

// Revenue-lens sibling to PackageOverviewContent — same reguler population,
// range picker, and type-scoped-by-route shape, but answers a different
// question ("which packages/memberships made the most money") instead of
// catalog health, so the columns/KPIs are financial (Klien Unik/Penjualan)
// rather than live-snapshot (Aktif/Kredit Tersisa/Akan Kedaluwarsa). Always
// reguler-scoped (`promo: "reguler"`, hardcoded below) — promo products have
// their own bounded-campaign counterpart, PendapatanProdukPromoContent.tsx.
const PendapatanProdukContent = ({ type, title, backTo }: PendapatanProdukContentProps) => {
  const { state: authState } = useAuth();
  const { sortState, onSortChange } = useTableSort();
  // Defaults to YTD — customRange starts pre-populated to match so the first
  // render already queries {from,to} instead of a never-sent range=ytd token.
  const ytdRange = getYtdDayRange();
  const [range, setRange] = useState<AnalyticsRange>("ytd");
  const [customRange, setCustomRange] = useState<CustomDayRange>(ytdRange);
  const isYtdActive = customRange?.from === ytdRange.from && customRange?.to === ytdRange.to;
  const searchInput = useRef<InputRef>(null);
  const { data: overview, isLoading } = getPackageAnalyticsOverview(
    range,
    type,
    "reguler",
    customRange,
    false,
    true,
    "sales",
  );

  const namespace = type === "membership" ? "membership" : "package";

  // Total Terjual is a plain client-side sum of the table's own `sold`
  // column — same light-aggregation precedent PendapatanProdukPromoContent
  // already uses for its own KPIs, kept in sync with the table by
  // construction (no separate backend field to drift).
  const totalSold = overview?.table.reduce((sum, row) => sum + row.sold, 0) ?? 0;

  // Columns come from the shared report catalogue (one name, width and
  // alignment per metric); this page keeps its own order, search and sorters.
  const columns: ReportColumn<PackageAnalyticsRow>[] = [
    reportColumn<PackageAnalyticsRow>(type === "membership" ? "membership" : "package", {
      dataIndex: "name",
      render: (_: string, row) => <ProductNameCell row={row} />,
      search: getColumnSearchProps<PackageAnalyticsRow, "name">({
        config: "name",
        searchInput,
      }),
    }),
    reportColumn<PackageAnalyticsRow>("unique_clients", {
      dataIndex: "unique_clients",
      sorter: (a, b) => a.unique_clients - b.unique_clients,
    }),
    reportColumn<PackageAnalyticsRow>("sold", {
      dataIndex: "sold",
      // Real sales only. Upgrades are counted separately: they are
      // expansion revenue rather than new units, but their fee is in
      // Penjualan and would otherwise have no visible source.
      info: () => translate(`analyticsPage.${namespace}.col_sold_info`),
      sorter: (a, b) => a.sold - b.sold,
    }),
    // Upgrades are not sales, but their fee IS inside Penjualan on this same
    // row, so the count has to be visible or that money has no source. Always
    // present rather than appearing with the data: a column that comes and
    // goes has to be gated identically on three surfaces, and the row cap
    // made the screen and the PDF disagree. A dash means none this period.
    // Paket only, since a membership can never be an upgrade target.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("upgrades", {
            dataIndex: "upgrades",
            render: (value: number) => <span style={tnum}>{formatUpgrades(value)}</span>,
            sorter: (a, b) => (a.upgrades ?? 0) - (b.upgrades ?? 0),
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("sales", {
      dataIndex: "sales",
      sorter: (a, b) => a.sales - b.sales,
      // Mirrors the server default (highest revenue first).
      defaultSortOrder: "descend",
    }),
    // Paket only — Membership has no credit-consumption concept. Paired
    // columns on purpose: Kredit Terpakai is the raw count, Pendapatan dari
    // Booking its prorated $ value, so a reader can sanity-check the money
    // figure against its own input instead of it looking like a bug.
    ...(type === "membership"
      ? []
      : [
          reportColumn<PackageAnalyticsRow>("credits_used", {
            dataIndex: "credits_used",
            sorter: (a, b) => a.credits_used - b.credits_used,
          }),
          reportColumn<PackageAnalyticsRow>("booking_revenue", {
            dataIndex: "earned_revenue",
            sorter: (a, b) => a.earned_revenue - b.earned_revenue,
          }),
        ]),
    reportColumn<PackageAnalyticsRow>("last_purchase", {
      dataIndex: "last_purchase",
      sorter: (a, b) => (a.last_purchase ?? "").localeCompare(b.last_purchase ?? ""),
    }),
  ];

  const pdfSpec = useMemo(() => pendapatanProdukSpec(type), [type]);
  const pageCount = useReportPageCount(pdfSpec, overview, sortState);

  return (
    <>
      <ReportPageHeading
        title={title}
        backTo={backTo}
        extra={
          <ExportMenu
            reportSlug={`pendapatan-${type}`}
            params={
              customRange ? { from_date: customRange.from, to_date: customRange.to } : { range }
            }
            pdf={
              overview && {
                rowChoice: {
                  pagesFor: pageCount.pagesFor,
                  prepare: pageCount.prepare,
                  available: Math.min(overview.table.length, MAX_PDF_ROWS),
                  total: overview.table.length,
                },
                fileName: `spott-pendapatan-${type}-${analyticsRangeStamp(range, customRange)}.pdf`,
                render: async (maxRows) => {
                  const { renderReportPdf } = await loadReportPdf();
                  return renderReportPdf({
                    slug: type === "membership" ? "pendapatan-membership" : "pendapatan-paket",
                    studio: authState.business?.name ?? "",
                    data: overview,
                    sortState,
                    maxRows,
                  });
                },
              }
            }
          />
        }
      />
      <Flex vertical gap={20}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <span style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {overview?.range_label}
          </span>
          <Flex align="center" gap={10} wrap="wrap">
            <CustomRangePicker value={isYtdActive ? null : customRange} onChange={setCustomRange} />
            <RangeSegmented
              value={isYtdActive ? "ytd" : range}
              active={!customRange || isYtdActive}
              onChange={(value) => {
                setCustomRange(value === "ytd" ? ytdRange : null);
                setRange(value);
              }}
            />
          </Flex>
        </Flex>
        <Row gutter={[16, 16]}>
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Col key={i} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: 16 }}>
                  <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
              </Col>
            ))
          ) : (
            <>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<WalletOutlined />}
                  title={translate("analyticsPage.sales.kpi_total_sales")}
                  information={translate("analyticsPage.columns.sales_info")}
                  value={formatCurrency(overview?.total_sales?.value ?? 0)}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<ShoppingOutlined />}
                  title={translate(`analyticsPage.${namespace}.kpi_purchases_count`)}
                  value={totalSold}
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <MetricCard
                  icon={<CrownOutlined />}
                  title={
                    overview?.best_seller_by_revenue?.revenue
                      ? `${translate(`analyticsPage.${namespace}.kpi_purchases_best_seller`)} · ${formatCurrency(overview.best_seller_by_revenue.revenue)}`
                      : translate(`analyticsPage.${namespace}.kpi_purchases_best_seller`)
                  }
                  value={overview?.best_seller_by_revenue?.name ?? "-"}
                  text
                />
              </Col>
            </>
          )}
        </Row>
        <Card styles={{ body: { padding: 18 } }}>
          <ReportTable<PackageAnalyticsRow>
            rowKey="package_id"
            loading={isLoading}
            columns={columns}
            dataSource={overview?.table ?? []}
            pageMemory={`analyticsPendapatanProduk.${type}`}
            onSortChange={onSortChange}
          />
        </Card>
      </Flex>
    </>
  );
};

export default PendapatanProdukContent;
